import React, { useEffect, useRef } from 'react';
import StatusCircle from './StatusCircle';
import ContactInfo from './ContactInfo';
import firebaseService from '../services/firebase';

const HeroSection = ({ shopData, showInstallButton, onInstallClick }) => {
  const hasTrackedInitialView = useRef(false);

  useEffect(() => {
    // Only track analytics on the very first load, not on Firebase updates
    if (shopData?.status && !hasTrackedInitialView.current) {
      firebaseService.incrementStatusView(shopData.status);
      hasTrackedInitialView.current = true;
    }
  }, [shopData?.status]);

  if (!shopData) {
    return (
      <section className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl">Loading shop status...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-2xl mx-auto">
        {/* Logo and Title Row */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="w-24 h-24 flex-shrink-0">
            {shopData.logo_url ? (
              <div className="w-full h-full rounded-full overflow-hidden bg-white p-2 shadow-lg">
                <img 
                  src={shopData.logo_url} 
                  alt={`${shopData.title} logo`}
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            ) : (
              <img 
                src="/logo.png" 
                alt="Barber Shop Logo"
                className="w-full h-full object-contain"
              />
            )}
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold">
            {shopData.title || 'Barber Shop'}
          </h1>
        </div>
        
        {/* Custom Message */}
        {shopData.custom_message && (
          <div className="mb-8">
            <p className="text-xl text-gray-300 max-w-md mx-auto text-center">
              {shopData.custom_message}
            </p>
          </div>
        )}

        {/* Status Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-400 mb-3 text-center">Current Status:</h3>
          <StatusCircle 
            status={shopData.status}
            statusTexts={shopData.status_texts}
          />
        </div>

        {/* Opening Hours */}
        {shopData.opening_hours && shopData.opening_hours.length > 0 && (
          <div className="mb-8 bg-gray-800 rounded-lg p-6 max-w-lg mx-auto">
            <h3 className="text-lg font-medium text-gray-400 mb-4 text-center">Opening Hours</h3>
            <div className="space-y-2">
              {(() => {
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const today = days[new Date().getDay()];
                
                return shopData.opening_hours.map((hour, index) => (
                  <div 
                    key={index}
                    className={`flex justify-between items-center py-2 px-3 rounded ${
                      hour.day === today ? 'bg-gray-700 font-semibold' : ''
                    }`}
                  >
                    <span className="text-gray-300">{hour.day}</span>
                    <span className="text-white">
                      {hour.open && hour.close && hour.open !== 'Closed' && hour.close !== 'Closed'
                        ? `${hour.open} - ${hour.close}` 
                        : 'Closed'
                      }
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="mb-8 max-w-lg mx-auto">
          <ContactInfo phone={shopData.phone} location={shopData.location} />
        </div>

        {/* Install Button */}
        {showInstallButton && (
          <div className="mt-8">
            <button
              onClick={onInstallClick}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-lg transition-colors"
            >
              {/* <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v6" />
              </svg> */}
              Add me to your Home Screen!
            </button>
          </div>
        )}

        {shopData.last_updated && (
          <p className="text-sm text-gray-500 mt-6">
            Last updated: {new Date(shopData.last_updated).toLocaleString('en-CY', {
              timeZone: 'Europe/Nicosia',
              hour12: false
            })}
          </p>
        )}
      </div>
    </section>
  );
};

export default HeroSection;